import { usePatchPickUpLocationBo } from "@/api/pickUpLocation.api";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { GetAllCompanyDto, PickupZone } from "@/types/api/api-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectV2 } from "@/components/ui/customs/select-v2";

type EditPickupZoneModalProps = {
  open: boolean;
  onClose: () => void;
  pickUpLocation?: GetAllCompanyDto["pickUpLocations"][number];
  pickupZonesList?: PickupZone[];
};

export const EditPickupZoneModal = ({
  open,
  onClose,
  pickUpLocation,
  pickupZonesList,
}: EditPickupZoneModalProps) => {
  const { t } = useTranslation(["boClients", "common"]);
  const [selectedPickupZone, setSelectedPickupZone] = useState("");

  const { mutateAsync: patchPickUpLocationBo, isPending } = usePatchPickUpLocationBo();

  useEffect(() => {
    if (open && pickUpLocation) {
      setSelectedPickupZone(pickUpLocation.pickupZones?.id ?? "none");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pickUpLocation?.id, pickUpLocation?.pickupZones?.id]);

  const handleUpdatePickupLocation = async () => {
    if (!pickUpLocation) return;
    await patchPickUpLocationBo({
      id: pickUpLocation.id,
      data: {
        pickupZonesId: selectedPickupZone,
      },
    });
    onClose();
  };

  const selectOptions = pickupZonesList
    ? [
        { label: t("pickupZoneEditModal.noZone"), value: "none" },
        ...pickupZonesList.map((el) => ({
          label: el.name,
          value: el.id,
        })),
      ]
    : [];

  return (
    <Modal
      open={open && !!pickUpLocation}
      onClose={onClose}
      title={t("pickupZoneEditModal.title")}
      description={pickUpLocation?.name}
      size="md"
      contentClassName="max-w-[600px]"
      footer={
        <>
          <Button
            type="button"
            onClick={handleUpdatePickupLocation}
            disabled={isPending}
            className="w-full"
          >
            {isPending ? <StyledSpinner /> : t("pickupZoneEditModal.submit")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={isPending}
            className="w-full"
          >
            {t("common:actions.cancel")}
          </Button>
        </>
      }
    >
      <div className="flex w-full flex-row items-center justify-between py-4">
        <div className="flex flex-1 flex-col">
          <p className="text-sm font-medium text-gray-600">
            {t("pickupZoneEditModal.pickupZoneLabel")}
          </p>
        </div>
        <SelectV2
          placeholder={t("pickupZoneEditModal.pickupZonePlaceholder")}
          clearable={false}
          searchable={false}
          onChange={(value) => setSelectedPickupZone(value)}
          value={selectedPickupZone}
          options={selectOptions}
        />
      </div>
    </Modal>
  );
};
